import { Fields } from 'common/form';
import { CampaignType } from 'generated-graphql/types';
import {
  GetSourcingSharedCampaignTypeOptionsInput,
  getSourcingSharedCampaignTypeOptions,
  shouldSourcingSharedShowCustomJobPostingUrl,
  isSourcingSharedUrlOnlyMode,
  isSourcingSharedUrlPreviewMode,
  isSourcingSharedUrlAddMode
} from 'modules/sourcing-shared/forms/utils';
import { SourcingSharedSetupApplicationFormData } from 'modules/sourcing-shared/forms';
import {
  SETUP_APPLICATION_PREVIEW_LINK_COPY,
  SETUP_APPLICATION_ADD_URL_COPY,
  SETUP_APPLICATION_DEFAULT_URL_COPY
} from 'modules/sourcing-shared/constants';
import { SxProps, Theme } from '@mui/material';

type GetSourcingSharedSetupApplicationFieldsProps = {
  campaignType: CampaignType;
  defaultCustomJobPostingUrl: string | undefined;
  isGeneratingPortalUrl: boolean;
} & GetSourcingSharedCampaignTypeOptionsInput;

const customJobPostingUrlInputSx: SxProps<Theme> = {
  '& input:focus::placeholder': {
    opacity: 0
  }
};
export const getSourcingSharedSetupApplicationFields = ({
  campaignType,
  defaultCustomJobPostingUrl,
  hasAtsJobId,
  hasIntegrationCredentials,
  atsProvider,
  portalUrl,
  customJobPostingUrl,
  isGeneratingPortalUrl,
  atsJobApplicationUrl,
  channelType
}: GetSourcingSharedSetupApplicationFieldsProps): Fields<SourcingSharedSetupApplicationFormData> => {
  const campaignTypeOptions = getSourcingSharedCampaignTypeOptions({
    hasAtsJobId,
    hasIntegrationCredentials,
    atsProvider,
    portalUrl,
    customJobPostingUrl,
    atsJobApplicationUrl,
    channelType
  });

  // Detect URL-only mode and its sub-modes
  const isUrlOnlyMode = isSourcingSharedUrlOnlyMode(campaignTypeOptions);
  const isUrlPreviewMode = isSourcingSharedUrlPreviewMode(
    campaignTypeOptions,
    portalUrl,
    customJobPostingUrl
  );
  const isUrlAddMode = isSourcingSharedUrlAddMode(
    campaignTypeOptions,
    portalUrl,
    customJobPostingUrl
  );

  // Determine the appropriate subLabel based on mode
  const getCustomJobPostingUrlSubLabel = () => {
    if (isUrlPreviewMode) {
      return SETUP_APPLICATION_PREVIEW_LINK_COPY;
    }
    if (isUrlAddMode) {
      return SETUP_APPLICATION_ADD_URL_COPY;
    }
    return SETUP_APPLICATION_DEFAULT_URL_COPY;
  };

  /**
   * Determines whether the custom job posting URL input should be hidden.
   * - Preview mode: hidden (only show preview link)
   * - Add mode: visible (user needs to enter URL)
   * - Otherwise: based on shouldSourcingSharedShowCustomJobPostingUrl logic
   */
  const getShouldHideCustomJobPostingUrlInput = (): boolean => {
    if (isUrlPreviewMode) {
      return true;
    }
    if (isUrlAddMode) {
      return false;
    }
    return !shouldSourcingSharedShowCustomJobPostingUrl({
      portalUrl,
      hasCustomJobPostingUrl: !!customJobPostingUrl,
      campaignType
    });
  };

  return {
    campaignType: {
      type: 'radio',
      label: 'How will candidates apply?',
      options: campaignTypeOptions,
      defaultValue: campaignType,
      isLoading: isGeneratingPortalUrl,
      hidden: isUrlOnlyMode
    },
    customJobPostingUrl: {
      type: 'text',
      label: 'Application URL',
      subLabel: getCustomJobPostingUrlSubLabel(),
      placeholder: 'https://www.example.com',
      sx: customJobPostingUrlInputSx,
      hidden: getShouldHideCustomJobPostingUrlInput(),
      defaultValue: defaultCustomJobPostingUrl
    }
  };
};
